export const personalInfo = {
  name: "Ahmad Abu Hussein",
  title: "Technical Lead & Senior Software Engineer",
  location: "Amman, Jordan",
  email: "ahmadabuhussein8@gmail.com",
  github: "https://github.com/AhmadRAbuhussein",
  linkedin: "https://www.linkedin.com/in/ahmad-abu-hussein-383996182/",
  resumeLink: "/Ahmad_Abu_Hussein_Resume.pdf",
  bio: "Tech Lead and Senior Software Engineer with 5+ years building and shipping full-stack, distributed systems across 6+ industries. I lead engineering teams, define system architecture, and own delivery from design to production — with a focus on clean code, scalable systems, and long-term maintainability.",
  roles: [
    "Technical Lead",
    "Senior Software Engineer",
    "Full-Stack Developer",
    ".NET & React Specialist",
    "System Architect",
  ],
};

export const experiences = [
  {
    id: 1,
    role: "Technical Lead",
    company: "Simplify9",
    period: "Jan 2026 – Present",
    current: true,
    points: [
      "Lead technical strategy and architectural direction across multiple enterprise-scale full-stack platforms",
      "Lead a cross-functional engineering team of 3–4 developers, driving architectural decisions and coordinated delivery",
      "Define system topology, service boundaries, and architectural standards",
      "Architect event-driven communication patterns using RabbitMQ",
      "Oversee end-to-end engineering execution from requirements through production release",
      "Represent engineering in strategic planning, aligning technical roadmap with business objectives",
    ],
    keyProjects: [
      "Bitween — open-source serverless integration platform (20+ adapters, 1000+ TPS)",
      "Macro Plate — meal delivery platform, Los Angeles",
    ],
  },
  {
    id: 2,
    role: "Senior Software Engineer",
    company: "Simplify9",
    period: "Dec 2024 – Jan 2026",
    current: false,
    points: [
      "Led architecture and delivery of full-stack platforms using .NET Core, C#, React, React Native",
      "Built a reusable workflow engine across Talmaro HR modules",
      "Delivered and deployed Mealivery production platform",
      "Led secure enterprise integration with 7X using AS2, REST APIs, event-driven communication",
      "Designed backend services for Gameezy's AI-driven analytics module, orchestrating LLM interactions and structured prompt strategies",
      "Established CI/CD workflows on Azure with Docker",
    ],
    keyProjects: [],
  },
  {
    id: 3,
    role: "Software Engineer",
    company: "Simplify9",
    period: "Sep 2021 – Dec 2024",
    current: false,
    points: [
      "Delivered full-stack applications across logistics, food & delivery, fintech, insurance, HR, and IoT",
      "Owned AutoParts digital marketplace connecting insurers, surveyors, workshops, and suppliers",
      "Supported Shipa platform: parcel tracking, payments, customer interactions",
      "Designed and optimized database schemas for PostgreSQL, MySQL, and MongoDB",
    ],
    keyProjects: [],
  },
  {
    id: 4,
    role: "Mobile Application Developer",
    company: "Smart Business Network",
    period: "Jan 2021 – Sep 2021",
    note: "Part-time",
    current: false,
    points: [
      "Developed Android applications using Java, covering the full mobile app lifecycle from design through testing",
      "Collaborated with the development team to design, implement, and ship user-facing features",
      "Gained hands-on experience with Android SDK, UI design patterns, and mobile performance practices",
    ],
    keyProjects: [],
  },
];

export const projects = [
  {
    id: 1,
    title: "Gameezy",
    subtitle: "AI-Powered Gaming Analytics Platform",
    description: "Co-founded the first Arabic gaming analytics platform featuring an AI-powered analysis engine (OpenAI) that delivers personalized performance insights, strengths/weaknesses breakdowns, and actionable recommendations. Supports Clash Royale, Dota 2, Chess.com, Clash of Clans, and League of Legends with global leaderboards and full Arabic/English localization.",
    stack: ["Next.js 15", "React 19", "TypeScript", ".NET 9", "Clean Architecture", "EF Core", "Docker", "OpenAI"],
    liveLink: "https://gameezy.gg",
    featured: true,
  },
  {
    id: 2,
    title: "Bitween",
    subtitle: "Open-Source Serverless Integration Platform",
    description: "Open-source serverless integration platform supporting 20+ ready-made adapters, handling 1,000+ transactions per second with multi-protocol system connectivity for enterprise clients.",
    stack: [".NET", "C#", "RabbitMQ", "Docker", "Microservices", "REST APIs"],
    liveLink: "https://bitween.io",
    githubLink: "https://github.com/simplify9",
    featured: true,
  },
  {
    id: 3,
    title: "Macro Plate",
    subtitle: "Subscription Meal Delivery — Los Angeles",
    description: "Led full frontend development of a live LA-based subscription meal delivery platform offering chef-crafted meal plans (Traditional, High Protein, Paleo, Vegetarian). Built the complete customer-facing React application: plan selection, subscription management, ordering flows, pause/cancel, and delivery scheduling.",
    stack: ["React", ".NET Core", "PostgreSQL", "Docker", "Azure"],
    liveLink: "https://macro-plate.com",
    featured: true,
  },
  {
    id: 4,
    title: "Mealivery",
    subtitle: "Meal Subscription Platform",
    description: "Technical Lead on a 4-person team delivering a full production meal subscription platform. Owned system architecture, technical decisions, and end-to-end delivery across customer ordering, subscription management, and operational workflows.",
    stack: [".NET Core", "React", "PostgreSQL", "Docker"],
    liveLink: "https://mealivery.com",
    featured: true,
  },
  {
    id: 5,
    title: "AutoParts (GIG)",
    subtitle: "Insurance Claims Marketplace",
    description: "Controlled digital marketplace connecting insurers, surveyors, workshops, and suppliers to transparently source vehicle spare parts during insurance claims.",
    stack: [".NET Core", "React", "PostgreSQL", "REST APIs"],
    featured: false,
  },
  {
    id: 6,
    title: "Talmaro HR",
    subtitle: "Enterprise HRMS Platform",
    description: "Enterprise HRMS platform with a reusable workflow engine supporting configurable approval flows and template-driven document generation across all HR modules.",
    stack: [".NET Core", "React", "Clean Architecture", "PostgreSQL"],
    featured: false,
  },
];

export const skillGroups = [
  {
    category: "Backend",
    skills: [
      "C#", ".NET Core", "ASP.NET", "Entity Framework Core",
      "Clean Architecture", "Microservices", "REST APIs",
      "GraphQL", "RabbitMQ", "Webhooks", "AS2", "Event-Driven Systems",
    ],
  },
  {
    category: "Frontend",
    skills: ["React.js", "React Native", "Next.js", "TypeScript", "JavaScript", "Tailwind CSS"],
  },
  {
    category: "Databases",
    skills: ["PostgreSQL", "MongoDB", "MySQL", "Redis", "Query Optimization"],
  },
  {
    category: "DevOps & Infrastructure",
    skills: [
      "Docker", "Docker Compose", "Kubernetes (AKS)",
      "Azure DevOps", "GitHub Actions", "CI/CD Pipelines",
      "Azure Functions", "Azure Blob Storage",
      "Nginx", "Linux", "SSH Deployments",
      "Grafana", "Sentry", "Elasticsearch",
    ],
  },
  {
    category: "AI / LLM",
    skills: ["OpenAI API", "Prompt Engineering", "Structured Output"],
  },
  {
    category: "Engineering Practices",
    skills: ["Software Architecture", "System Design", "TDD", "Unit Testing", "Git"],
  },
];

export const education = [
  {
    degree: "Bachelor's in Software Engineering",
    institution: "University of Petra",
    location: "Amman, Jordan",
    period: "2018 – 2022",
  },
];
